import { db } from "../db/index.js";
import { RESOURCE_TYPES } from "@edu-quizz/shared/constants/resourceTypes";

/**
 * Calculate the next review date based on quality rating
 * @param {number} quality - 1 (easy), 2 (medium), 3 (hard)
 * @returns {string} ISO date string for next review
 */
export function calculateNextReview(quality) {
    // Simple spaced repetition algorithm
    // quality 1 (easy): review in 7 days
    // quality 2 (medium): review in 3 days
    // quality 3 (hard): review in 1 day
    const daysToAdd = quality === 1 ? 7 : quality === 2 ? 3 : 1;
    const nextReview = new Date();
    nextReview.setDate(nextReview.getDate() + daysToAdd);
    return nextReview.toISOString();
}

/**
 * Save a flashcard review in spaced repetition mode
 * Only keeps one review per card x deck combination (last review replaces previous ones)
 * Always sets fullyLearned to false (cards can be reviewed multiple times)
 * @param {Object} deck - The flashcard deck
 * @param {Object} card - The current card
 * @param {number} level - Difficulty level (1-3)
 * @returns {Promise<void>}
 */
export async function saveReviewSpaced(deck, card, level) {
    try {
        // Check if a review already exists for this card x deck combination
        const existingReview = await db.flashcardReviews
            .where("flashcardCardServerId")
            .equals(card.serverId)
            .and((review) => review.flashcardDeckResourceServerId === deck.serverResourceId)
            .first();

        // Delete existing review if found
        if (existingReview) {
            await db.flashcardReviews.delete(existingReview.uuid);
        }

        // Always generate a new UUID
        const uuid = crypto.randomUUID();
        const reviewData = {
            uuid,
            flashcardDeckResourceServerId: deck.serverResourceId,
            flashcardCardServerId: card.serverId,
            quality: level,
            reviewedAt: new Date().toISOString(),
            nextReview: calculateNextReview(level),
            fullyLearned: false, // Spaced mode: never fully learned
            playMode: 'spaced', // Track that this review was done in spaced mode
        };

        // Add new review
        await db.flashcardReviews.add(reviewData);

        // Add to sync queue
        await db.addToSyncQueue(
            "create",
            RESOURCE_TYPES.FLASHCARD_DECK,
            deck.serverResourceId,
            reviewData,
            "flashcardReviews", // Target local table name
            uuid, // Target local row UUID
        );
    } catch (err) {
        console.error("Error saving flashcard review (spaced):", err);
        throw err;
    }
}

/**
 * Save a flashcard review in intensive mode
 * Only keeps one review per card x deck combination (last review replaces previous ones)
 * Sets fullyLearned to true when level is 1 (easy)
 * @param {Object} deck - The flashcard deck
 * @param {Object} card - The current card
 * @param {number} level - Difficulty level (1-3)
 * @returns {Promise<void>}
 */
export async function saveReviewIntensive(deck, card, level) {
    try {
        // Check if a review already exists for this card x deck combination
        const existingReview = await db.flashcardReviews
            .where("flashcardCardServerId")
            .equals(card.serverId)
            .and((review) => review.flashcardDeckResourceServerId === deck.serverResourceId)
            .first();

        // Delete existing review if found
        if (existingReview) {
            await db.flashcardReviews.delete(existingReview.uuid);
        }

        // Always generate a new UUID
        const uuid = crypto.randomUUID();
        const isFullyLearned = level === 1; // Easy = fully learned
        const reviewData = {
            uuid,
            flashcardDeckResourceServerId: deck.serverResourceId,
            flashcardCardServerId: card.serverId,
            quality: level,
            reviewedAt: new Date().toISOString(),
            nextReview: isFullyLearned ? null : calculateNextReview(level),
            fullyLearned: isFullyLearned, // Intensive mode: true if easy
            playMode: 'intensive', // Track that this review was done in intensive mode
        };

        // Add new review
        await db.flashcardReviews.add(reviewData);

        // Add to sync queue
        await db.addToSyncQueue(
            "create",
            RESOURCE_TYPES.FLASHCARD_DECK,
            deck.serverResourceId,
            reviewData,
            "flashcardReviews", // Target local table name
            uuid, // Target local row UUID
        );
    } catch (err) {
        console.error("Error saving flashcard review (intensive):", err);
        throw err;
    }
}

/**
 * Load deck with all cards for intensive study mode
 * Keeps all cards but tracks which ones are fully learned
 * @param {number} deckId - The deck ID
 * @returns {Promise<{deck: Object, cards: Array, fullyLearnedCardIds: Set}>}
 */
export async function loadDeckIntensive(deckId) {
    // Get the flashcard deck with its cards from the database
    const deckData = await db.getFlashcardDeckWithCards(deckId);

    if (!deckData) {
        throw new Error("Deck non trouvé");
    }

    const allCards = deckData.cards || [];
    const cardsToStudy = [];
    const fullyLearnedCardIds = new Set();

    // Intensive mode: include all cards, but track which ones are fully learned
    for (const card of allCards) {
        // Get the last review for this card
        const reviews = await db.flashcardReviews
            .where("flashcardCardServerId")
            .equals(card.serverId)
            .sortBy('reviewedAt');
        const lastReview = reviews.length > 0 ? reviews[reviews.length - 1] : null;

        // Track cards that are fully learned
        if (lastReview && lastReview.fullyLearned) {
            fullyLearnedCardIds.add(card.serverId);
        }

        // Include all cards
        const isReversed = card.studyBothSides ? Math.random() < 0.5 : false;
        cardsToStudy.push({
            ...card,
            isReversed,
        });
    }

    return {
        deck: deckData,
        cards: cardsToStudy,
        fullyLearnedCardIds,
    };
}

/**
 * Load deck with cards due for review in spaced repetition mode
 * @param {number} deckId - The deck ID
 * @returns {Promise<{deck: Object, cards: Array}>}
 */
export async function loadDeckSpaced(deckId) {
    // Get the flashcard deck with its cards from the database
    const deckData = await db.getFlashcardDeckWithCards(deckId);

    if (!deckData) {
        throw new Error("Deck non trouvé");
    }

    const allCards = deckData.cards || [];
    const now = new Date();
    const cardsDueForReview = [];

    // Spaced mode: show cards due for review
    for (const card of allCards) {
        // Get the last review for this card
        const reviews = await db.flashcardReviews
            .where("flashcardCardServerId")
            .equals(card.serverId)
            .sortBy('reviewedAt');
        const lastReview = reviews.length > 0 ? reviews[reviews.length - 1] : null;

        // Include card if:
        // 1. Never reviewed before, OR
        // 2. nextReview date has passed
        if (!lastReview || new Date(lastReview.nextReview) <= now) {
            // If studyBothSides is true, randomly decide if the card should be reversed
            const isReversed = card.studyBothSides ? Math.random() < 0.5 : false;
            cardsDueForReview.push({
                ...card,
                isReversed,
            });
        }
    }

    return {
        deck: deckData,
        cards: cardsDueForReview,
    };
}

/**
 * Check if any card in the deck has been reviewed
 * @param {Array} cards - Array of flashcard cards
 * @returns {Promise<boolean>}
 */
export async function hasAnyReview(cards) {
    for (const card of cards) {
        const lastReview = await db.flashcardReviews
            .where("flashcardCardServerId")
            .equals(card.serverId)
            .first();
        if (lastReview) {
            return true;
        }
    }
    return false;
}

/**
 * Reset all reviews for a deck
 * @param {number} deckId - The deck ID
 * @returns {Promise<void>}
 */
export async function resetDeckReviews(deckId) {
    const deckData = await db.getFlashcardDeckWithCards(deckId);

    if (deckData && deckData.cards) {
        // Get all flashcard IDs from this deck
        const flashcardIds = deckData.cards.map((card) => card.serverId);

        // Delete all reviews for these flashcards
        for (const flashcardServerId of flashcardIds) {
            await db.flashcardReviews
                .where("flashcardCardServerId")
                .equals(flashcardServerId)
                .delete();
        }

        console.log(`Deleted reviews for ${flashcardIds.length} flashcards`);

        // Add reset action to sync queue
        const uuid = crypto.randomUUID();
        await db.addToSyncQueue(
            "reset_deck",
            RESOURCE_TYPES.FLASHCARD_DECK,
            deckData.serverResourceId,
            {},
            "flashcardReviews", // Target local table name
            null,
        );
    }
}

/**
 * Recycle non-completed cards in intensive mode
 * @param {Array} cards - All cards
 * @param {Set} completedCardIds - Set of completed card IDs
 * @returns {Array} - Shuffled array of non-completed cards
 */
export function recycleNonCompletedCards(cards, completedCardIds) {
    // Get cards that haven't been marked as easy
    const nonCompletedCards = cards.filter(
        card => !completedCardIds.has(card.serverId)
    );

    if (nonCompletedCards.length > 0) {
        // Re-randomize reversed state for non-completed cards
        return nonCompletedCards.map(card => ({
            ...card,
            isReversed: card.studyBothSides ? Math.random() < 0.5 : false,
        }));
    }

    return [];
}

/**
 * Get statistics about remaining cards to review in spaced mode
 * @param {number} deckId - The deck ID
 * @returns {Promise<{remainingCards: number, nextUnlockDate: string|null}>}
 * - remainingCards: number of cards that have been reviewed but are not yet due
 * - nextUnlockDate: ISO date string of the earliest card that will become available
 */
export async function getSpacedReviewStats(deckId) {
    const deckData = await db.getFlashcardDeckWithCards(deckId);

    if (!deckData) {
        throw new Error("Deck non trouvé");
    }

    const allCards = deckData.cards || [];
    const now = new Date();
    let remainingCards = 0;
    let nextUnlockDate = null;

    for (const card of allCards) {
        const reviews = await db.flashcardReviews
            .where("flashcardCardServerId")
            .equals(card.serverId)
            .sortBy('reviewedAt');
        const lastReview = reviews.length > 0 ? reviews[reviews.length - 1] : null;

        // If card has a review and is not yet due
        if (lastReview && new Date(lastReview.nextReview) > now) {
            remainingCards++;

            // Track the earliest next review date
            const cardNextReview = new Date(lastReview.nextReview);
            if (!nextUnlockDate || cardNextReview < new Date(nextUnlockDate)) {
                nextUnlockDate = lastReview.nextReview;
            }
        }
    }

    return {
        remainingCards,
        nextUnlockDate
    };
}
